import { FC, useMemo } from 'react';
import Field from 'components/Field/Field';
import useOrganization from 'state/contexts/Organization/useOrganization';
import formatMoney from 'utils/numbers/formatMoney';
import { getUserFriendlyBytes } from 'utils/bytes';
import { useUrl } from 'utils/hooks';
import { Link } from '@hologram-dimension/link';
import useUserInfo from 'hooks/User/useUserInfo';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import { LegacyPlan, LegacyPoolPlan } from 'types/legacyDashboard';
import styles from './PlanDetailsHeader.module.css';

interface PlanDetailsHeaderProps {
  plan: LegacyPlan;
  simInventoryBaseUrl: string;
  staticPool?: LegacyPoolPlan;
  simCount: number;
}

const PlanDetailsHeader: FC<PlanDetailsHeaderProps> = ({
  plan,
  simInventoryBaseUrl,
  staticPool,
  simCount,
}) => {
  const organizationId = useOrganization();

  const { planName, perLineCharge, dataLimit, variablerate, overageCharge, id } = plan;

  const activeSimsUrl = useUrl({
    url: `${simInventoryBaseUrl}/active`,
    replacers: [{ placeholder: 'orgid', value: organizationId?.toString() ?? '' }],
  });
  const predeploymentSimsUrl = useUrl({
    url: `${simInventoryBaseUrl}/predeployment`,
    replacers: [{ placeholder: 'orgid', value: organizationId?.toString() ?? '' }],
  });
  const { releaseFlags } = useUserInfo();
  const hasPredeployment = releaseFlags.has(ReleaseFlag.FEATURE_PREDEPLOYMENT);

  const includedData = useMemo(() => {
    if (staticPool) {
      return `${getUserFriendlyBytes(staticPool.data)} data pool`;
    }
    return dataLimit === 0 ? 'Pay as you go' : getUserFriendlyBytes(dataLimit);
  }, [staticPool, dataLimit]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <Field label="Name" value={planName} direction="column" valueClassname={styles.nameValue} />
      </div>
      <div className={styles.headerRow}>
        <Field
          label="Active SIMs"
          value={<Link to={`${activeSimsUrl}?plans=${id}`}>{simCount.toLocaleString()}</Link>}
          direction="column"
        />
        {hasPredeployment && (
          <Field
            label="Pre-deployment SIMs"
            value={<Link to={`${predeploymentSimsUrl}?plans=${id}`}>View</Link>}
            direction="column"
          />
        )}
        <Field label="Included data" value={includedData} direction="column" />
        <Field
          label={dataLimit !== 0 || staticPool ? 'Overage per MB' : 'Usage per MB'}
          value={variablerate ? 'Varies by network' : formatMoney(overageCharge)}
          direction="column"
        />
        <Field
          label={staticPool ? 'Per SIM MRC' : 'Monthly recurring charge'}
          value={formatMoney(perLineCharge)}
          direction="column"
        />
        {staticPool && (
          <Field
            label="Pool data MRC"
            value={formatMoney(parseFloat(staticPool.amount))}
            direction="column"
          />
        )}
        <Field label="Plan ID" value={id} direction="column" />
      </div>
    </div>
  );
};

export default PlanDetailsHeader;
